/**
 * PostMessage Service
 * Secure iframe communication with origin validation and type-safe messaging
 */

import {
  MessageType,
} from '@/types';
import type {
  WidgetMessage,
  BaseMessage,
  MessageHandler,
  MessageValidationResult,
  HandshakeRequestMessage,
  HandshakeCompleteMessage,
} from '@/types';
import {
  WidgetHandshakeService,
  type HandshakeCallbacks,
} from './handshake';

/**
 * PostMessage service configuration
 */
interface MessagingConfig {
  /** Allowed parent origins */
  allowedOrigins: string[];

  /** Enable debug logging */
  debug?: boolean;

  /** Target window (defaults to parent) */
  targetWindow?: Window;

  /** Require handshake before accepting messages (default: true) */
  requireHandshake?: boolean;
}

/**
 * PostMessage service for secure widget communication
 */
export class MessagingService {
  private allowedOrigins: Set<string>;
  private messageHandlers: Map<MessageType, Set<MessageHandler>>;
  private targetWindow: Window;
  private debug: boolean;
  private messageIdCounter: number = 0;
  private handshake: WidgetHandshakeService | null = null;
  private requireHandshake: boolean;

  constructor(config: MessagingConfig) {
    this.allowedOrigins = new Set(config.allowedOrigins);
    this.messageHandlers = new Map();
    this.targetWindow = config.targetWindow || window.parent;
    this.debug = config.debug || false;
    // Require handshake in production, optional in development
    this.requireHandshake = config.requireHandshake !== false;

    // Initialize handshake service
    if (this.requireHandshake) {
      const handshakeCallbacks: HandshakeCallbacks = {
        onComplete: (token) => {
          this.log('Handshake completed', { token });
        },
        onError: (error) => {
          console.error('[MessagingService] Handshake failed:', error);
        },
      };
      this.handshake = new WidgetHandshakeService(
        { timeout: 5000, debug: this.debug },
        handshakeCallbacks
      );
    }

    // Start listening for messages
    this.startListening();

    this.log('MessagingService initialized', {
      allowedOrigins: Array.from(this.allowedOrigins),
      requireHandshake: this.requireHandshake,
    });
  }

  /**
   * Start listening for postMessage events
   */
  private startListening(): void {
    window.addEventListener('message', this.handleMessage.bind(this), false);
  }

  /**
   * Stop listening for postMessage events
   */
  public stopListening(): void {
    window.removeEventListener('message', this.handleMessage.bind(this), false);
  }

  /**
   * Handle incoming postMessage events
   */
  private async handleMessage(event: MessageEvent): Promise<void> {
    // Validate origin
    if (!this.isOriginAllowed(event.origin)) {
      this.log('Rejected message from unauthorized origin', {
        origin: event.origin,
        allowedOrigins: Array.from(this.allowedOrigins),
      });
      return;
    }

    // Validate message structure
    const validationResult = this.validateMessage(event.data);
    if (!validationResult.valid) {
      this.log('Invalid message received', {
        error: validationResult.error,
        data: event.data,
      });
      return;
    }

    const message = validationResult.message!;

    // Handle handshake messages first (before checking if handshake is complete)
    if (this.handshake && this.isHandshakeMessage(message.type)) {
      await this.handleHandshakeMessage(message, event.origin);
      return;
    }

    // If handshake is required, check if it's complete
    if (this.requireHandshake && this.handshake && !this.handshake.isComplete()) {
      this.log('Rejected message - handshake not complete', {
        type: message.type,
        handshakeState: this.handshake.getState(),
      });
      return;
    }

    this.log('Message received', { type: message.type, data: message });

    // Get handlers for this message type
    const handlers = this.messageHandlers.get(message.type);
    if (!handlers || handlers.size === 0) {
      this.log('No handlers registered for message type', { type: message.type });
      return;
    }

    // Execute all handlers
    for (const handler of handlers) {
      try {
        await handler(message);
      } catch (error) {
        console.error('Error in message handler:', error);
        this.log('Handler error', { type: message.type, error });
      }
    }
  }

  /**
   * Validate message structure
   */
  private validateMessage(data: unknown): MessageValidationResult {
    if (!data || typeof data !== 'object') {
      return { valid: false, error: 'Message must be an object' };
    }

    const message = data as Partial<BaseMessage>;

    if (!message.type) {
      return { valid: false, error: 'Message must have a type' };
    }

    if (!message.timestamp) {
      return { valid: false, error: 'Message must have a timestamp' };
    }

    if (!message.messageId) {
      return { valid: false, error: 'Message must have a messageId' };
    }

    if (!message.origin) {
      return { valid: false, error: 'Message must have an origin' };
    }

    return { valid: true, message: message as WidgetMessage };
  }

  /**
   * Check if origin is allowed
   */
  private isOriginAllowed(origin: string): boolean {
    // Allow same origin
    if (origin === window.location.origin) {
      return true;
    }

    // Check against allowed origins
    // WARNING: Wildcard should only be used in development
    if (this.allowedOrigins.has('*')) {
      if (import.meta.env.PROD) {
        console.warn('[MessagingService] ⚠️ WARNING: Wildcard origin allowed in production!');
      }
      return true;
    }

    return this.allowedOrigins.has(origin);
  }

  /**
   * Check if message is a handshake message
   */
  private isHandshakeMessage(type: MessageType): boolean {
    return (
      type === MessageType.HANDSHAKE_REQUEST ||
      type === MessageType.HANDSHAKE_RESPONSE ||
      type === MessageType.HANDSHAKE_COMPLETE
    );
  }

  /**
   * Handle handshake messages
   */
  private async handleHandshakeMessage(
    message: WidgetMessage,
    eventOrigin: string
  ): Promise<void> {
    if (!this.handshake) {
      return;
    }

    switch (message.type) {
      case MessageType.HANDSHAKE_REQUEST: {
        const request = message as HandshakeRequestMessage;
        const response = this.handshake.handleHandshakeRequest(request, eventOrigin);
        if (response) {
          this.send(MessageType.HANDSHAKE_RESPONSE, response.data);
        }
        break;
      }

      case MessageType.HANDSHAKE_COMPLETE: {
        const complete = message as HandshakeCompleteMessage;
        this.handshake.handleHandshakeComplete(complete, eventOrigin);
        break;
      }

      default:
        // Handshake response is handled by parent, not widget
        break;
    }
  }

  /**
   * Register a message handler
   */
  public on<T extends WidgetMessage = WidgetMessage>(
    type: MessageType,
    handler: MessageHandler<T>
  ): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }

    this.messageHandlers.get(type)!.add(handler as MessageHandler);

    this.log('Handler registered', { type });

    // Return unsubscribe function
    return () => {
      this.messageHandlers.get(type)?.delete(handler as MessageHandler);
      this.log('Handler unregistered', { type });
    };
  }

  /**
   * Send a message to parent window
   */
  public send<T extends Partial<BaseMessage>>(
    type: MessageType,
    data?: Omit<T, 'type' | 'timestamp' | 'messageId' | 'origin'>
  ): void {
    const message: BaseMessage = {
      type,
      timestamp: new Date().toISOString(),
      messageId: this.generateMessageId(),
      origin: window.location.origin,
      ...data,
    };

    // Log message details for debugging
    this.log('📤 Sending message', { 
      type, 
      targetWindow: this.targetWindow === window.parent ? 'parent' : 'other',
      currentOrigin: window.location.origin,
      messageOrigin: message.origin,
      allowedOrigins: Array.from(this.allowedOrigins),
      messageStructure: {
        hasType: !!message.type,
        hasTimestamp: !!message.timestamp,
        hasMessageId: !!message.messageId,
        hasOrigin: !!message.origin,
        hasData: 'data' in message
      }
    });

    // Determine target origin
    let targetOrigin: string | null = null;

    // If handshake is complete, use parent origin from handshake
    if (this.handshake && this.handshake.isComplete()) {
      const parentOrigin = this.handshake.getParentOrigin();
      if (parentOrigin) {
        targetOrigin = parentOrigin;
      }
    }

    // Fallback: use allowed origins
    if (!targetOrigin) {
      if (this.allowedOrigins.has('*')) {
        // WARNING: Only for development
        if (import.meta.env.PROD) {
          console.warn('[MessagingService] ⚠️ WARNING: Using wildcard origin in production!');
        }
        this.targetWindow.postMessage(message, '*');
        this.log('✅ Message sent with wildcard target', { 
          type, 
          message: {
            type: message.type,
            timestamp: message.timestamp,
            messageId: message.messageId,
            origin: message.origin,
            data: (message as any).data
          }
        });
        return;
      } else {
        // Use first allowed origin (should only be one in production)
        targetOrigin = Array.from(this.allowedOrigins)[0] || null;
      }
    }

    // Send to specific origin
    if (targetOrigin) {
      try {
        this.targetWindow.postMessage(message, targetOrigin);
        this.log('✅ Message sent to specific origin', { 
          type, 
          origin: targetOrigin, 
          message: {
            type: message.type,
            timestamp: message.timestamp,
            messageId: message.messageId,
            origin: message.origin,
            data: (message as any).data
          }
        });
      } catch (error) {
        console.error('❌ Error sending message to origin:', targetOrigin, error);
      }
    } else {
      console.error('❌ No target origin available for message', { type });
    }
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    const timestamp = Date.now();
    const counter = this.messageIdCounter++;
    return `msg_${timestamp}_${counter}`;
  }

  /**
   * Add allowed origin at runtime
   */
  public addAllowedOrigin(origin: string): void {
    this.allowedOrigins.add(origin);
    this.log('Origin added', { origin });
  }

  /**
   * Remove allowed origin
   */
  public removeAllowedOrigin(origin: string): void {
    this.allowedOrigins.delete(origin);
    this.log('Origin removed', { origin });
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: unknown): void {
    if (this.debug) {
      console.log(`[MessagingService] ${message}`, data || '');
    }
  }

  /**
   * Cleanup and destroy service
   */
  public destroy(): void {
    this.stopListening();
    this.messageHandlers.clear();
    this.log('Service destroyed');
  }
}

/**
 * Create singleton messaging service instance
 */
let messagingServiceInstance: MessagingService | null = null;

/**
 * Initialize messaging service
 */
export function initMessagingService(config: MessagingConfig): MessagingService {
  if (messagingServiceInstance) {
    messagingServiceInstance.destroy();
  }

  messagingServiceInstance = new MessagingService(config);
  return messagingServiceInstance;
}

/**
 * Get messaging service instance
 */
export function getMessagingService(): MessagingService {
  if (!messagingServiceInstance) {
    throw new Error('MessagingService not initialized. Call initMessagingService first.');
  }
  return messagingServiceInstance;
}

/**
 * Destroy messaging service
 */
export function destroyMessagingService(): void {
  if (messagingServiceInstance) {
    messagingServiceInstance.destroy();
    messagingServiceInstance = null;
  }
}
